import { ActivitySnapshotStatus } from '../../services/apollo/inopack-graphql-schema';

// Single source of truth for how an activity's snapshot status reads in
// Spanish, shared by the diff panel, the Historial accordion, the activities
// list and the live snackbar.
//
// The status exists because two NULL snapshot columns used to mean three
// different things. Rendering BY STATUS rather than by "are the JSON fields
// empty" is the point: an empty diff after a failed capture is not the same
// message as an empty diff on a row written before the feature existed, and
// only one of the two is a warning.

/**
 * The neutral or warning line to show INSTEAD of a diff, or null when the
 * activity has a real before/after to render.
 */
export function getSnapshotStatusMessage(
    status: ActivitySnapshotStatus,
): string | null {
    switch (status) {
        case ActivitySnapshotStatus.CaptureFailed:
            // Deliberately says the operation SUCCEEDED first. The reader's
            // record is safe; what failed is the history of it, and nothing
            // here should read as "your save may not have worked".
            return 'La operación se guardó, pero no fue posible capturar el historial detallado.';
        case ActivitySnapshotStatus.NotSupported:
            return 'El historial detallado no está disponible para este tipo de registro.';
        case ActivitySnapshotStatus.Legacy:
            return 'Esta actividad es anterior al historial detallado.';
        default:
            return null;
    }
}

/**
 * Whether this activity should carry a visible warning marker.
 *
 * Only `capture_failed` qualifies. `legacy` and `not_supported` are ordinary
 * facts about a row, not problems — flagging them would train the reader to
 * ignore the marker that does mean something.
 */
// Accepts a bare string as well as the enum: the snackbar carries the status
// through a shared message type that must not depend on generated GraphQL
// types, and it arrives there as a plain string.
export function isSnapshotCaptureFailed(
    status: ActivitySnapshotStatus | string | null | undefined,
): boolean {
    return status === ActivitySnapshotStatus.CaptureFailed;
}

/** Tooltip for the compact warning marker in lists. */
export const SNAPSHOT_CAPTURE_FAILED_HINT =
    'La operación se guardó, pero no se pudo capturar su historial detallado.';
